/* eslint-disable @reatom/unit-naming-rule */
import { action, concurrent, sleep } from '@reatom/framework'
import i18next from 'i18next'

import { Data, pipe, Result } from '@blade/std-lib'
import { userReceivedAction } from '@root/entities/auth/atoms'
import { joinedInviteCodeAtom } from '@root/entities/referral'
import { ApiError } from '@root/shared/api'
import { AuthErrorCode } from '@root/shared/api/api-error-code'
import { reatomRequest, tryOrFailSilently } from '@root/shared/lib'
import { showSnackbarAction } from '@root/shared/ui-kit'

import {
  checkUsernameRequestAction,
  registerUserRequestDevAction,
  registerWithGoogleRequestAction,
  registerWithTelegramRequestAction,
  registerWithTwitterRequestAction,
} from './auth-requests'
import { USERNAME_KEY } from './constants'

import type { Redacted } from '@blade/std-lib'
import type { OAuthCode, TelegramAuthToken, User } from '@root/entities/auth'
import type { InviteCode } from '@root/entities/referral'
import type { Error } from '@root/shared'

export type RegistrationMethod =
  | {
      type: 'telegram'
      token: TelegramAuthToken
      code: OAuthCode
      referralCode: InviteCode | undefined
    }
  | {
      type: 'google'
      code: OAuthCode
      referralCode: InviteCode | undefined
    }
  | {
      type: 'twitter'
      code: OAuthCode
      referralCode: InviteCode | undefined
    }
  | {
      type: 'dev-username'
      referralCode: InviteCode | undefined
    }

export type RegistrationPayload = (
  | {
      type: 'telegram'
      token: TelegramAuthToken
      code: OAuthCode
    }
  | {
      type: 'google'
      code: OAuthCode
    }
  | {
      type: 'twitter'
      code: OAuthCode
    }
  | {
      type: 'dev-username'
    }
) & {
  password: Redacted.Redacted
  username: string
  referralCode: InviteCode | undefined
}

class UsernameUnavailableError extends Data.TaggedError(
  'UsernameUnavailableError',
) {}

const CHECK_USERNAME_DEBOUNCE_DELAY = 300

export const reatomRegistration = () => {
  const registerInternalAction = action(
    async (
      ctx,
      payload: RegistrationPayload,
    ): Promise<
      Result.Result<User.User, UsernameUnavailableError | Error.UnknownError>
    > => {
      const processRegistration = () => {
        if (payload.type === 'google') {
          const { password, username, code, referralCode } = payload
          return registerWithGoogleRequestAction(ctx, {
            password,
            username,
            inviteCode: referralCode,
            googleAuthCode: code,
          })
        }

        if (payload.type === 'twitter') {
          const { password, username, code, referralCode } = payload
          return registerWithTwitterRequestAction(ctx, {
            password,
            username,
            inviteCode: referralCode,
            authCode: code,
          })
        }

        if (payload.type === 'telegram') {
          const { password, username, code, token, referralCode } = payload

          return registerWithTelegramRequestAction(ctx, {
            password,
            username,
            inviteCode: referralCode,
            authCode: code,
            token,
          })
        }

        const { password, username, referralCode } = payload
        return registerUserRequestDevAction(ctx, {
          inviteCode: referralCode,
          password,
          username,
        })
      }

      const result = await ctx.schedule(() => processRegistration())

      return result
    },
    'registerInternalAction',
  )
  const registerStateAtom = reatomRequest({
    execAction: registerInternalAction,
    onResult: (ctx, result) => {
      if (Result.isSuccess(result)) {
        tryOrFailSilently(() => {
          localStorage.setItem(USERNAME_KEY, result.success.username)
        })
        userReceivedAction(ctx, result.success)
        joinedInviteCodeAtom(ctx, undefined)
        return
      }

      if (ApiError.isOfCode(AuthErrorCode.UserAlreadyExists)(result.failure)) {
        // Handled inside screen
        return
      }

      showSnackbarAction(ctx, {
        text: i18next.t('common.something-went-wrong'),
        intent: 'error',
      })
    },
  })

  const checkIsUsernameAvailableInternalAction = action(
    concurrent(async (ctx, username: string) => {
      if (username.length < 3) {
        return Result.succeed(true)
      }

      await ctx.schedule(() => sleep(CHECK_USERNAME_DEBOUNCE_DELAY))

      return pipe(
        await ctx.schedule(() => checkUsernameRequestAction(ctx, username)),
        Result.map((it) => it.isAvailable),
      )
    }),
    'checkIsUsernameAvailableInternalAction',
  )

  const checkIsUsernameAvailableStateAtom = reatomRequest({
    execAction: checkIsUsernameAvailableInternalAction,
  })

  return {
    registerAction: registerStateAtom.execute,
    registerStateAtom,
    checkIsUsernameAvailableAction: checkIsUsernameAvailableStateAtom.execute,
    checkIsUsernameAvailableStateAtom,
  }
}
export type RegistrationDesktopModel = ReturnType<typeof reatomRegistration>
